// react/src/pages/Messages/MessageDialog.tsx

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Dialog } from 'src/components/Dialog';
import { Typography } from 'src/components/Typography';
import {
  DeliveryMethod,
  MediaUpload,
  Recipient,
} from 'src/models/conversation/_types';
import { User } from 'src/models/user/_types';
import {
  MessageRecipientScope,
  UserInput,
} from 'src/pages/Messages/NewConversation/UserInput';
import { useNewConversation } from 'src/pages/Messages/NewConversation/useNewConversation';

import { NewMessageInput } from '../../pages/Messages/NewMessageInput';

interface MessageDialogProps {
  open: boolean;
  title?: string;
  onClose: () => void;
  defaultMessage?: string;
  defaultUser?: Pick<User, 'id' | 'nickname'>;
  defaultUsers?: Pick<User, 'id' | 'nickname'>[];
  onSend?: (
    message: string,
    files: MediaUpload[],
    delivery?: DeliveryMethod[]
  ) => void;
  disableSendButton?: boolean;
  /** Limit recipient picker (e.g. notify-staff → platform trainers only). */
  recipientScope?: MessageRecipientScope;
}

export const MessageDialog: React.FC<MessageDialogProps> = ({
  open,
  onClose,
  title,
  defaultMessage = '',
  defaultUser,
  defaultUsers,
  onSend,
  disableSendButton,
  recipientScope = 'all',
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'dialog' });
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });
  const {
    toUsers,
    setToUsers,
    sendMessage: defaultSendMessage,
  } = useNewConversation(onClose);

  const [message, setMessage] = useState(defaultMessage);
  const [showUserInput, setShowUserInput] = useState(
    !defaultUser && !defaultUsers?.length
  );

  useEffect(() => {
    setMessage(defaultMessage);
  }, [defaultMessage]);

  useEffect(() => {
    if (defaultUsers?.length) {
      setToUsers(
        defaultUsers.map((user) => ({
          id: user.id.toString(),
          itemType: 'User',
          nickname: user.nickname,
          type: 'User',
        }))
      );
      setShowUserInput(false);
    } else if (defaultUser) {
      setToUsers([
        {
          id: defaultUser.id.toString(),
          itemType: 'User',
          nickname: defaultUser.nickname,
          type: 'User',
        },
      ]);
      setShowUserInput(false);
    } else {
      setToUsers([]);
      setShowUserInput(true);
    }
  }, [defaultUser, defaultUsers, setToUsers]);

  const handleSend = (options: {
    newMessage: string;
    files: MediaUpload[];
    sendAt?: Date | null;
    delivery?: DeliveryMethod[];
    subject?: string;
  }) => {
    const { newMessage, files, sendAt, delivery, subject } = options;
    if (onSend) {
      onSend(newMessage, files, delivery);
    } else {
      defaultSendMessage({
        newMessage,
        files,
        sendAt,
        delivery,
        subject,
      });
    }
  };

  const handleUserChange = (
    e: React.SyntheticEvent | undefined,
    newUsers: Recipient[]
  ) => {
    setToUsers(newUsers);
  };

  const recipients = defaultUsers?.length
    ? defaultUsers.map((user) => user.nickname).join(', ')
    : defaultUser?.nickname;

  return (
    <Dialog open={open} onClose={onClose} title={title || t('messageTitle')}>
      <Box sx={{ mb: 2 }}>
        {showUserInput ? (
          <UserInput
            onChange={handleUserChange}
            recipientScope={recipientScope}
            value={toUsers}
          />
        ) : (
          <Typography variant="body1">
            {tCommon('labels.to')}: {recipients}
          </Typography>
        )}
      </Box>
      <NewMessageInput
        sendMessage={handleSend}
        onScheduleMessage={undefined}
        defaultMessage={message}
        hasRecipient={true}
        disableSendButton={disableSendButton}
      />
    </Dialog>
  );
};

interface OpenMessageDialogOptions {
  recipientId: string;
  recipientNickname?: string;
  message?: string;
  title?: string;
}

export function useMessageDialog() {
  const [isOpen, setIsOpen] = useState(false);
  const [defaultMessage, setDefaultMessage] = useState('');
  const [defaultUser, setDefaultUser] =
    useState<Pick<User, 'id' | 'nickname'>>();
  const [dialogTitle, setDialogTitle] = useState<string>();

  const openMessageDialog = ({
    recipientId,
    recipientNickname,
    message,
    title,
  }: OpenMessageDialogOptions) => {
    setDefaultUser({ id: recipientId, nickname: recipientNickname ?? '' });
    setDefaultMessage(message ?? '');
    setDialogTitle(title);
    setIsOpen(true);
  };

  const closeMessageDialog = () => {
    setIsOpen(false);
    setDefaultMessage('');
    setDefaultUser(undefined);
    setDialogTitle(undefined);
  };

  const MessageDialogComponent = () => (
    <MessageDialog
      open={isOpen}
      onClose={closeMessageDialog}
      defaultMessage={defaultMessage}
      defaultUser={defaultUser}
      title={dialogTitle}
    />
  );

  return {
    openMessageDialog,
    closeMessageDialog,
    MessageDialogComponent,
  };
}
